import React from "react"

interface Project {
  id: string
  name: string
  description?: string
  createdAt: string
  role: number
}

interface ProjectsListProps {
  projects: Project[]
  isLoading: boolean
  onProjectSelect: (projectId: string) => void
  selectedProjectId: string | null
}

const getRoleName = (role: number) => {
  switch (role) {
    case 0:
      return "Owner"
    case 1:
      return "Admin"
    case 2:
      return "Member"
    case 3:
      return "Viewer"
    default:
      return "Unknown"
  }
}

const getRoleColor = (role: number) => {
  switch (role) {
    case 0:
      return "bg-red-100 text-red-800"
    case 1:
      return "bg-blue-100 text-blue-800"
    case 2:
      return "bg-green-100 text-green-800"
    case 3:
      return "bg-gray-100 text-gray-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

export const ProjectsList: React.FC<ProjectsListProps> = ({
  projects,
  isLoading,
  onProjectSelect,
  selectedProjectId,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-card rounded-lg border p-4 animate-pulse">
            <div className="h-5 bg-muted rounded mb-2" />
            <div className="h-4 bg-muted rounded w-3/4 mb-2" />
            <div className="h-3 bg-muted rounded w-1/2" />
          </div>
        ))}
      </div>
    )
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📋</div>
        <h3 className="text-lg font-medium text-foreground mb-2">No projects yet</h3>
        <p className="text-muted-foreground">Create your first project to get started</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {projects.map((project) => (
        <div
          key={project.id}
          onClick={() => onProjectSelect(project.id)}
          className={`bg-card rounded-lg border p-4 cursor-pointer transition-colors hover:bg-secondary ${
            selectedProjectId === project.id ? "ring-2 ring-primary bg-secondary" : ""
          }`}
        >
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-semibold text-foreground">{project.name}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(project.role)}`}>
              {getRoleName(project.role)}
            </span>
          </div>
          {project.description && <p className="text-muted-foreground text-sm mb-2">{project.description}</p>}
          <p className="text-xs text-muted-foreground">Created {new Date(project.createdAt).toLocaleDateString()}</p>
        </div>
      ))}
    </div>
  )
}
